import { adminSupabase } from '../services/supabaseService';

/**
 * Creates the copy_trades table if it doesn't exist
 * This table is needed for the copy trader to log trade history
 */
export const createCopyTradesTable = async (): Promise<boolean> => {
    try {
        console.log('🔧 Creating copy_trades table...');

        const migrationSQL = `
-- Create copy_trades table (Transaction History)
-- This table stores the history of executed copy trades
CREATE TABLE IF NOT EXISTS public.copy_trades (
    id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
    
    -- User and rule information
    user_id UUID REFERENCES public.users(id) ON DELETE CASCADE,
    wallet_address TEXT NOT NULL,
    rule_id UUID REFERENCES public.copy_pool_subscribers(id) ON DELETE CASCADE,
    rule_name TEXT NOT NULL,
    
    -- Source transaction information
    source_wallet TEXT NOT NULL,
    source_transaction_signature TEXT,
    source_transaction_data JSONB,
    
    -- Copy trade execution information
    trading_wallet_address TEXT NOT NULL,
    transaction_type TEXT NOT NULL CHECK (transaction_type IN ('buy', 'sell')),
    
    -- Token information
    token_address TEXT NOT NULL,
    token_symbol TEXT,
    token_name TEXT,
    
    -- Trade amounts and pricing
    amount DECIMAL(20, 9) NOT NULL, -- SOL amount
    token_amount DECIMAL(30, 9), -- Token amount received/sold
    price_usd DECIMAL(20, 9), -- USD price at time of trade
    slippage_percent DECIMAL(5, 2), -- Actual slippage experienced
    
    -- Execution results
    status TEXT NOT NULL DEFAULT 'pending' CHECK (status IN ('pending', 'success', 'failed')),
    transaction_signature TEXT, -- Resulting transaction signature
    error_message TEXT, -- Error message if failed
    
    -- Timing information
    copy_delay_applied INTEGER DEFAULT 0, -- Actual delay applied in seconds
    execution_time_ms INTEGER, -- Time taken to execute the trade
    
    -- Additional metadata
    trade_context JSONB DEFAULT '{}', -- Additional context about the trade
    
    -- Timestamps
    created_at TIMESTAMPTZ DEFAULT now(),
    completed_at TIMESTAMPTZ,
    
    -- Performance and analytics
    profit_loss_usd DECIMAL(20, 9), -- P&L in USD (calculated later)
    profit_loss_sol DECIMAL(20, 9)  -- P&L in SOL (calculated later)
);

-- Create indexes for better query performance
CREATE INDEX IF NOT EXISTS idx_copy_trades_wallet_address ON public.copy_trades(wallet_address);
CREATE INDEX IF NOT EXISTS idx_copy_trades_rule_id ON public.copy_trades(rule_id);
CREATE INDEX IF NOT EXISTS idx_copy_trades_status ON public.copy_trades(status);
CREATE INDEX IF NOT EXISTS idx_copy_trades_transaction_type ON public.copy_trades(transaction_type);
CREATE INDEX IF NOT EXISTS idx_copy_trades_token_address ON public.copy_trades(token_address);
CREATE INDEX IF NOT EXISTS idx_copy_trades_source_wallet ON public.copy_trades(source_wallet);
CREATE INDEX IF NOT EXISTS idx_copy_trades_created_at ON public.copy_trades(created_at DESC);
CREATE INDEX IF NOT EXISTS idx_copy_trades_source_tx_sig ON public.copy_trades(source_transaction_signature);

-- Enable Row Level Security
ALTER TABLE public.copy_trades ENABLE ROW LEVEL SECURITY;

-- RLS Policies for copy_trades
-- Users can view their own copy trades
DO $$
BEGIN
    IF NOT EXISTS (
        SELECT 1 FROM pg_policies 
        WHERE tablename = 'copy_trades' 
        AND policyname = 'Users can view their own copy trades'
    ) THEN
        CREATE POLICY "Users can view their own copy trades" 
        ON public.copy_trades FOR SELECT 
        TO authenticated 
        USING ((SELECT wallet_address FROM public.users WHERE id = user_id) = auth.jwt() ->> 'sub');
    END IF;
END $$;

-- Allow inserts for copy trade logging
DO $$
BEGIN
    IF NOT EXISTS (
        SELECT 1 FROM pg_policies 
        WHERE tablename = 'copy_trades' 
        AND policyname = 'Allow inserts for copy trade logging'
    ) THEN
        CREATE POLICY "Allow inserts for copy trade logging" 
        ON public.copy_trades FOR INSERT 
        TO authenticated
        WITH CHECK (true);
    END IF;
END $$;

-- Allow updates for status changes
DO $$
BEGIN
    IF NOT EXISTS (
        SELECT 1 FROM pg_policies 
        WHERE tablename = 'copy_trades' 
        AND policyname = 'Allow updates for copy trade status'
    ) THEN
        CREATE POLICY "Allow updates for copy trade status" 
        ON public.copy_trades FOR UPDATE 
        TO authenticated
        USING ((SELECT wallet_address FROM public.users WHERE id = user_id) = auth.jwt() ->> 'sub')
        WITH CHECK ((SELECT wallet_address FROM public.users WHERE id = user_id) = auth.jwt() ->> 'sub');
    END IF;
END $$;

-- Create function to update completed_at when status changes to success/failed
CREATE OR REPLACE FUNCTION update_copy_trade_completed_at()
RETURNS TRIGGER AS $$
BEGIN
  IF NEW.status IN ('success', 'failed') AND OLD.status = 'pending' THEN
    NEW.completed_at = now();
  END IF;
  RETURN NEW;
END;
$$ LANGUAGE plpgsql;

-- Create trigger for completed_at updates
DROP TRIGGER IF EXISTS update_copy_trade_completed_at_trigger ON public.copy_trades;
CREATE TRIGGER update_copy_trade_completed_at_trigger
BEFORE UPDATE ON public.copy_trades
FOR EACH ROW
EXECUTE FUNCTION update_copy_trade_completed_at();
    `;

        // Execute the SQL directly using the raw query method
        const { error } = await adminSupabase.rpc('exec_sql', { sql: migrationSQL });

        if (error) {
            console.error('❌ Failed to create copy_trades table:', error);
            return false;
        }

        console.log('✅ Successfully created copy_trades table');
        return true;
    } catch (error) {
        console.error('❌ Exception creating copy_trades table:', error);
        return false;
    }
};

/**
 * Check if the copy_trades table exists
 */
export const checkCopyTradesTableExists = async (): Promise<boolean> => {
    try {
        const { data, error } = await adminSupabase
            .from('copy_trades')
            .select('id', { count: 'exact', head: true });

        if (error) {
            // If the error is about the table not existing, return false
            if (error.message.includes('does not exist') || error.message.includes('relation') || error.code === '42P01') {
                return false;
            }
            // For other errors, log them but assume table exists
            console.warn('⚠️ Error checking copy_trades table:', error);
            return true;
        }

        return true;
    } catch (error) {
        console.warn('⚠️ Exception checking copy_trades table:', error);
        return false;
    }
};

/**
 * Ensure the copy_trades table exists, create it if it doesn't
 */
export const ensureCopyTradesTable = async (): Promise<boolean> => {
    const exists = await checkCopyTradesTableExists();

    if (!exists) {
        console.log('📊 copy_trades table does not exist, creating it...');
        return await createCopyTradesTable();
    }

    console.log('✅ copy_trades table already exists');
    return true;
}; 